import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiClock, FiPlay, FiPause, FiRefreshCw, FiAward, FiInfo } = FiIcons;

const TimerChallenge = () => {
  const navigate = useNavigate();
  const [gameState, setGameState] = useState('setup'); // 'setup', 'playing', 'paused', 'completed'
  const [selectedTime, setSelectedTime] = useState(60);
  const [timeLeft, setTimeLeft] = useState(60);
  const [currentChallenge, setCurrentChallenge] = useState(null);
  const [challengeIndex, setChallengeIndex] = useState(0);
  const [completedChallenges, setCompletedChallenges] = useState([]);
  const [gameLevel, setGameLevel] = useState('mild');
  const [showPrivacy, setShowPrivacy] = useState(false);

  const challenges = {
    mild: [
      "Hôn nhau trong 10 giây liên tục",
      "Massage vai cho đối phương trong 30 giây",
      "Ôm chặt và thì thầm lời yêu thương",
      "Nhảy một điệu nhảy chậm cùng nhau",
      "Hát một bài hát tình yêu ngắn",
      "Kể 3 điều yêu thích về đối phương",
      "Vẽ trái tim lên da bằng ngón tay",
      "Hôn khắp khuôn mặt đối phương",
      "Massage tay trong 20 giây",
      "Nhìn vào mắt nhau trong 15 giây"
    ],
    spicy: [
      "Hôn cổ đối phương trong 20 giây",
      "Cởi một món đồ một cách quyến rũ",
      "Vuốt ve cơ thể đối phương trong 30 giây",
      "Hôn và cắn nhẹ tai đối phương",
      "Nhảy lap dance trong 25 giây",
      "Liếm từ cổ xuống vai đối phương",
      "Massage ngực/lưng trong 30 giây",
      "Hôn môi say đắm trong 20 giây",
      "Dùng lưỡi vẽ đường trên da",
      "Thì thầm điều khiêu khích vào tai"
    ],
    hot: [
      "Cởi đồ của đối phương bằng răng",
      "Dùng miệng khám phá cơ thể trong 30 giây",
      "Làm đối phương kích thích bằng tay",
      "Thực hiện striptease trong 45 giây",
      "Liếm kem từ cơ thể đối phương",
      "Massage vùng nhạy cảm trong 20 giây",
      "Hôn khắp cơ thể trong 40 giây",
      "Dùng lưỡi kích thích đối phương",
      "Cởi hết đồ và tạo dáng",
      "Để đối phương làm bất cứ gì trong 30 giây"
    ]
  };

  const timeOptions = [30, 60, 90, 120, 180, 300];

  useEffect(() => {
    let interval;
    if (gameState === 'playing' && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            setGameState('completed');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [gameState, timeLeft]);

  const startGame = () => {
    setTimeLeft(selectedTime);
    setCurrentChallenge(challenges[gameLevel][0]);
    setChallengeIndex(0);
    setCompletedChallenges([]);
    setGameState('playing');
  };

  const pauseGame = () => {
    setGameState('paused');
  };

  const resumeGame = () => {
    setGameState('playing');
  };

  const completeChallenge = () => {
    const newCompleted = [...completedChallenges, currentChallenge];
    setCompletedChallenges(newCompleted);
    
    const nextIndex = (challengeIndex + 1) % challenges[gameLevel].length;
    setChallengeIndex(nextIndex);
    setCurrentChallenge(challenges[gameLevel][nextIndex]);
  };

  const resetGame = () => {
    setGameState('setup');
    setTimeLeft(selectedTime);
    setCurrentChallenge(null);
    setChallengeIndex(0);
    setCompletedChallenges([]);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'mild': return 'from-green-400 to-blue-400';
      case 'spicy': return 'from-yellow-400 to-orange-400';
      case 'hot': return 'from-red-400 to-pink-500';
      default: return 'from-green-400 to-blue-400';
    }
  };

  const getLevelEmoji = (level) => {
    switch (level) {
      case 'mild': return '😊';
      case 'spicy': return '😏';
      case 'hot': return '🔥';
      default: return '😊';
    }
  };

  if (showPrivacy) {
    return (
      <div className="min-h-screen p-4 flex flex-col">
        <motion.button
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => setShowPrivacy(false)}
          className="self-start mb-4 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all"
        >
          <SafeIcon icon={FiArrowLeft} className="text-xl" />
        </motion.button>
        
        <div className="flex-1 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl w-full bg-white/10 backdrop-blur-lg p-8 rounded-3xl"
          >
            <h1 className="text-3xl font-bold text-white text-center mb-6">
              Điều Khoản & Quyền Riêng Tư
            </h1>
            
            <div className="space-y-6 text-white">
              <div>
                <h2 className="text-xl font-bold mb-2">1. Giới Hạn Độ Tuổi:</h2>
                <p className="text-pink-200">
                  Ứng dụng này chỉ dành cho người từ 18 tuổi trở lên. Bằng việc sử dụng ứng dụng này, bạn xác nhận mình đã đủ tuổi theo quy định.
                </p>
              </div>
              
              <div>
                <h2 className="text-xl font-bold mb-2">2. Quyền Sở Hữu:</h2>
                <p className="text-pink-200">
                  Game này được tạo và sở hữu độc quyền bởi Lux Homestay. Mọi hình thức sao chép, phân phối lại mà không có sự cho phép đều bị nghiêm cấm.
                </p>
              </div>
              
              <div>
                <h2 className="text-xl font-bold mb-2">3. Nội Dung:</h2>
                <p className="text-pink-200">
                  Trò chơi có chứa nội dung người lớn và chỉ nên chơi trong không gian riêng tư. Người dùng tự chịu trách nhiệm về việc sử dụng trò chơi này.
                </p>
              </div>
              
              <div>
                <h2 className="text-xl font-bold mb-2">4. Quyền Riêng Tư:</h2>
                <p className="text-pink-200">
                  Chúng tôi không thu thập, lưu trữ hay chia sẻ bất kỳ thông tin cá nhân nào của người dùng. Trò chơi được chơi hoàn toàn ngoại tuyến.
                </p>
              </div>
              
              <div>
                <h2 className="text-xl font-bold mb-2">5. Miễn Trừ Trách Nhiệm:</h2>
                <p className="text-pink-200">
                  Lux Homestay không chịu trách nhiệm về bất kỳ hậu quả nào phát sinh từ việc sử dụng trò chơi này. Người dùng tự chịu trách nhiệm về quyết định và hành động của mình.
                </p>
              </div>
              
              <div className="pt-4 border-t border-white/20">
                <p className="text-center text-pink-200">
                  © 2023 Lux Homestay. Bảo lưu mọi quyền.
                </p>
              </div>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowPrivacy(false)}
                className="w-full mt-4 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-bold py-4 px-6 rounded-2xl shadow-lg transition-all duration-300"
              >
                Tôi Đã Hiểu & Đồng Ý
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  if (gameState === 'setup') {
    return (
      <div className="min-h-screen p-4 flex flex-col">
        <motion.button
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate('/')}
          className="self-start mb-4 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all"
        >
          <SafeIcon icon={FiArrowLeft} className="text-xl" />
        </motion.button>

        <div className="flex-1 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-md w-full bg-white/10 backdrop-blur-lg p-8 rounded-3xl"
          >
            <h1 className="text-4xl font-bold text-white text-center mb-6">
              ⏰ Timer Challenge ⏰
            </h1>

            <div className="space-y-6">
              <div>
                <label className="block text-pink-200 text-sm font-bold mb-2">
                  Chọn mức độ:
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {['mild', 'spicy', 'hot'].map((level) => (
                    <button
                      key={level}
                      onClick={() => setGameLevel(level)}
                      className={`p-3 rounded-xl text-sm font-medium transition-all ${
                        gameLevel === level
                          ? `bg-gradient-to-r ${getLevelColor(level)} text-white`
                          : 'bg-white/10 text-white/80 hover:bg-white/20'
                      }`}
                    >
                      {getLevelEmoji(level)}{' '}
                      {level === 'mild' ? 'Nhẹ' : level === 'spicy' ? 'Cay' : 'Nóng'}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-pink-200 text-sm font-bold mb-2">
                  Thời gian chơi:
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {timeOptions.map((time) => (
                    <button
                      key={time}
                      onClick={() => setSelectedTime(time)}
                      className={`p-3 rounded-xl text-sm font-medium transition-all ${
                        selectedTime === time
                          ? 'bg-pink-500 text-white'
                          : 'bg-white/10 text-white/80 hover:bg-white/20'
                      }`}
                    >
                      {time < 60 ? `${time}s` : `${time / 60}m`}
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-pink-500/20 p-4 rounded-xl">
                <h3 className="text-white font-medium mb-2 text-center">Hướng Dẫn Chơi</h3>
                <p className="text-pink-200 text-sm">
                  Hoàn thành càng nhiều thử thách càng tốt trong thời gian quy định. 
                  Mỗi thử thách sẽ xuất hiện ngẫu nhiên và bạn có thể tạm dừng game bất cứ lúc nào.
                </p>
              </div>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={startGame}
                className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-bold py-4 px-8 rounded-2xl shadow-lg transition-all duration-300 flex items-center justify-center gap-2"
              >
                <SafeIcon icon={FiPlay} className="text-xl" />
                Bắt Đầu Challenge!
              </motion.button>

              <div className="flex items-center justify-between">
                <button
                  onClick={() => setShowPrivacy(true)}
                  className="text-pink-200 text-xs underline flex items-center"
                >
                  <SafeIcon icon={FiInfo} className="mr-1 text-xs" />
                  Điều khoản & Quyền riêng tư
                </button>
                <p className="text-pink-200 text-xs">
                  © 2023 Lux Homestay
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  if (gameState === 'completed') {
    return (
      <div className="min-h-screen p-4 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full bg-white/10 backdrop-blur-lg p-8 rounded-3xl text-center"
        >
          <h1 className="text-4xl font-bold text-white mb-6">
            🎉 Thời Gian Kết Thúc! 🎉
          </h1>

          <div className="bg-gradient-to-r from-yellow-400 to-orange-500 p-6 rounded-2xl mb-6">
            <SafeIcon icon={FiAward} className="text-4xl text-white mb-2" />
            <h2 className="text-2xl font-bold text-white mb-2">
              Hoàn Thành: {completedChallenges.length} Thử Thách
            </h2>
            <p className="text-white/90">
              Trong {selectedTime < 60 ? `${selectedTime} giây` : `${selectedTime / 60} phút`}
            </p>
          </div>

          <div className="space-y-4">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={resetGame}
              className="w-full bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 text-white font-bold py-3 px-6 rounded-xl shadow-lg transition-all duration-300 flex items-center justify-center gap-2"
            >
              <SafeIcon icon={FiRefreshCw} />
              Chơi Lại
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/')}
              className="w-full bg-white/20 hover:bg-white/30 text-white font-bold py-3 px-6 rounded-xl transition-all duration-300"
            >
              Về Menu Chính
            </motion.button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 flex flex-col">
      <motion.button
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        onClick={() => navigate('/')}
        className="self-start mb-4 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all"
      >
        <SafeIcon icon={FiArrowLeft} className="text-xl" />
      </motion.button>

      <div className="flex-1 flex flex-col items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-6 rounded-3xl mb-6">
            <SafeIcon icon={FiClock} className="text-6xl text-white mb-4" />
            <h1 className="text-4xl font-bold text-white mb-2">
              {formatTime(timeLeft)}
            </h1>
            <p className="text-pink-200">
              Hoàn thành: {completedChallenges.length} thử thách
            </p>
          </div>
        </motion.div>

        <div className="max-w-md w-full">
          <AnimatePresence mode="wait">
            {currentChallenge && (
              <motion.div
                key={challengeIndex}
                initial={{ opacity: 0, rotateY: -90, scale: 0.8 }}
                animate={{ opacity: 1, rotateY: 0, scale: 1 }}
                exit={{ opacity: 0, rotateY: 90, scale: 0.8 }}
                transition={{ duration: 0.4 }}
                className={`bg-gradient-to-br ${getLevelColor(gameLevel)} p-8 rounded-3xl shadow-2xl text-white relative overflow-hidden min-h-[250px] flex flex-col justify-center mb-8`}
              >
                <div className="absolute top-4 right-4 text-2xl">
                  {getLevelEmoji(gameLevel)}
                </div>
                
                <div className="text-center">
                  <p className="text-2xl leading-relaxed font-medium">
                    {currentChallenge}
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex gap-4 mb-6">
            {gameState === 'playing' ? (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={pauseGame}
                className="flex-1 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white font-bold py-4 px-4 rounded-2xl shadow-lg flex items-center justify-center gap-2 text-lg transition-all duration-300"
              >
                <SafeIcon icon={FiPause} className="text-xl" />
                Tạm Dừng
              </motion.button>
            ) : (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={resumeGame}
                className="flex-1 bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 text-white font-bold py-4 px-4 rounded-2xl shadow-lg flex items-center justify-center gap-2 text-lg transition-all duration-300"
              >
                <SafeIcon icon={FiPlay} className="text-xl" />
                Tiếp Tục
              </motion.button>
            )}

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={completeChallenge}
              className="flex-1 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-bold py-4 px-4 rounded-2xl shadow-lg flex items-center justify-center gap-2 text-lg transition-all duration-300"
            >
              <SafeIcon icon={FiAward} className="text-xl" />
              Hoàn Thành
            </motion.button>
          </div>

          <div className="text-center">
            <p className="text-pink-200 text-xs">
              © 2023 Lux Homestay
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimerChallenge;